import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsContext = createContext();

// Configuration des devises
const CURRENCY_CONFIG = {
  EUR: { code: 'EUR', symbol: '€', locale: 'fr-FR', position: 'after' },
  USD: { code: 'USD', symbol: '$', locale: 'en-US', position: 'before' },
  GBP: { code: 'GBP', symbol: '£', locale: 'en-GB', position: 'before' },
  CHF: { code: 'CHF', symbol: 'Fr.', locale: 'fr-CH', position: 'after' },
  XOF: { code: 'XOF', symbol: 'FCFA', locale: 'fr-FR', position: 'after' },
};

export const SettingsProvider = ({ children }) => {
  const { token } = useAuth();
  const [settings, setSettings] = useState({
    preferences: {
      devise: 'EUR',
      format_date: 'DD/MM/YYYY'
    }
  });
  const [loading, setLoading] = useState(true);

  // Charger les paramètres utilisateur
  const fetchSettings = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Formatter un montant selon la devise choisie
  const formatMontant = useCallback((value, options = {}) => {
    const devise = settings?.preferences?.devise || 'EUR';
    const config = CURRENCY_CONFIG[devise] || CURRENCY_CONFIG.EUR;
    
    // Pour XOF (FCFA), on n'utilise pas les décimales
    const minimumFractionDigits = devise === 'XOF' ? 0 : 2;
    const maximumFractionDigits = devise === 'XOF' ? 0 : 2;
    
    try {
      // Essayer d'utiliser Intl.NumberFormat avec le code devise
      if (devise !== 'XOF') {
        return new Intl.NumberFormat(config.locale, {
          style: 'currency',
          currency: config.code,
          minimumFractionDigits,
          maximumFractionDigits
        }).format(value);
      } else {
        // Pour FCFA, formatter manuellement car XOF n'est pas toujours bien supporté
        const formatted = new Intl.NumberFormat('fr-FR', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value);
        return `${formatted} FCFA`;
      }
    } catch (e) {
      // Fallback en cas d'erreur
      const formatted = new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits,
        maximumFractionDigits
      }).format(value);
      return config.position === 'before' 
        ? `${config.symbol}${formatted}`
        : `${formatted} ${config.symbol}`;
    }
  }, [settings?.preferences?.devise]);

  // Formatter un montant avec signe (+/-)
  const formatMontantAvecSigne = useCallback((value) => {
    const devise = settings?.preferences?.devise || 'EUR';
    const config = CURRENCY_CONFIG[devise] || CURRENCY_CONFIG.EUR;
    const minimumFractionDigits = devise === 'XOF' ? 0 : 2;
    const maximumFractionDigits = devise === 'XOF' ? 0 : 2;
    
    const absValue = Math.abs(value);
    let formatted;
    
    if (devise === 'XOF') {
      formatted = new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(absValue);
      formatted = `${formatted} FCFA`;
    } else {
      try {
        formatted = new Intl.NumberFormat(config.locale, {
          style: 'currency',
          currency: config.code,
          minimumFractionDigits,
          maximumFractionDigits
        }).format(absValue);
      } catch (e) {
        formatted = new Intl.NumberFormat('fr-FR', {
          minimumFractionDigits,
          maximumFractionDigits
        }).format(absValue);
        formatted = config.position === 'before' 
          ? `${config.symbol}${formatted}`
          : `${formatted} ${config.symbol}`;
      }
    }
    
    if (value > 0) return `+ ${formatted}`;
    if (value < 0) return `- ${formatted}`;
    return formatted;
  }, [settings?.preferences?.devise]);

  // Obtenir le symbole de la devise
  const getCurrencySymbol = useCallback(() => {
    const devise = settings?.preferences?.devise || 'EUR';
    return CURRENCY_CONFIG[devise]?.symbol || '€';
  }, [settings?.preferences?.devise]);

  // Obtenir le code de la devise
  const getCurrencyCode = useCallback(() => {
    return settings?.preferences?.devise || 'EUR';
  }, [settings?.preferences?.devise]);

  // Mettre à jour les paramètres localement (pour synchronisation immédiate)
  const updateLocalSettings = useCallback((newSettings) => {
    setSettings(newSettings);
  }, []);

  const value = {
    settings,
    loading,
    fetchSettings,
    updateLocalSettings,
    formatMontant,
    formatMontantAvecSigne,
    getCurrencySymbol,
    getCurrencyCode
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
